import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";

const normalizeLegendColor = (raw) => {
  if (typeof raw !== "string") return null;
  const v = raw.trim();

  if (v.startsWith("rgba(")) {
    const inside = v.slice(5, -1);
    const parts = inside.split(",").map((p) => p.trim());
    if (parts.length >= 4) {
      return `rgb(${parts[0]}, ${parts[1]}, ${parts[2]})`;
    }
  }

  if (v.startsWith("hsla(")) {
    const inside = v.slice(5, -1);
    const parts = inside.split(",").map((p) => p.trim());
    if (parts.length >= 4) {
      return `hsl(${parts[0]}, ${parts[1]}, ${parts[2]})`;
    }
  }

  return v;
};

const truncateLabel = (value, maxLen = 12) => {
  const str = value == null ? "" : String(value);
  if (!maxLen || str.length <= maxLen) return str;
  return `${str.slice(0, Math.max(0, maxLen - 1))}…`;
};

const formatCompactNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? "");
  const abs = Math.abs(n);
  if (abs < 1000) return `${n}`;
  return new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n);
};

const BarChart = ({ data, options }) => {
  const labelCount = Array.isArray(data?.labels) ? data.labels.length : 0;
  const minWidth = labelCount > 0 ? Math.max(520, labelCount * 60) : undefined;

  const datasets = Array.isArray(data?.datasets) ? data.datasets : [];

  const densityDefaults = useMemo(() => {
    if (labelCount <= 1) {
      return { categoryPercentage: 0.35, barPercentage: 0.6, maxBarThickness: 64 };
    }
    if (labelCount === 2) {
      return { categoryPercentage: 0.5, barPercentage: 0.7, maxBarThickness: 72 };
    }
    return { categoryPercentage: 0.8, barPercentage: 0.9, maxBarThickness: 96 };
  }, [labelCount]);

  const normalizedData = useMemo(() => {
    const raw = data || {};
    const rawSets = Array.isArray(raw?.datasets) ? raw.datasets : [];
    return {
      ...raw,
      datasets: rawSets.map((ds) => {
        const next = { ...ds };
        if (typeof next.categoryPercentage !== "number") {
          next.categoryPercentage = densityDefaults.categoryPercentage;
        }
        if (typeof next.barPercentage !== "number") {
          next.barPercentage = densityDefaults.barPercentage;
        }
        if (typeof next.maxBarThickness !== "number") {
          next.maxBarThickness = densityDefaults.maxBarThickness;
        }
        return next;
      }),
    };
  }, [data, densityDefaults]);

  const chartRef = useRef(null);
  const [yTicks, setYTicks] = useState([]);
  const [y1Ticks, setY1Ticks] = useState([]);
  const [axisPadding, setAxisPadding] = useState({ top: 0, bottom: 0 });

  const hasRightAxis = Boolean(options?.scales?.y1);

  useEffect(() => {
    const chart = chartRef.current;
    const y = chart?.scales?.y;
    const y1 = chart?.scales?.y1;
    const chartArea = chart?.chartArea;

    const nextYTicks = Array.isArray(y?.ticks)
      ? y.ticks
          .map((t, i) => ({
            label: t?.label ?? t?.value ?? "",
            y: typeof y.getPixelForTick === "function" ? y.getPixelForTick(i) : null,
          }))
          .filter((t) => t.label !== "" && typeof t.y === "number")
      : [];

    const nextY1Ticks = Array.isArray(y1?.ticks)
      ? y1.ticks
          .map((t, i) => ({
            label: t?.label ?? t?.value ?? "",
            y: typeof y1.getPixelForTick === "function" ? y1.getPixelForTick(i) : null,
          }))
          .filter((t) => t.label !== "" && typeof t.y === "number")
      : [];

    const nextPadding = { top: 0, bottom: 0 };
    if (chartArea && typeof chart?.height === "number") {
      nextPadding.top = Math.max(0, chartArea.top);
      nextPadding.bottom = Math.max(0, chart.height - chartArea.bottom);
    }

    setYTicks(nextYTicks);
    setY1Ticks(nextY1Ticks);
    setAxisPadding(nextPadding);
  }, [data, options]);

  const mergedOptions = useMemo(() => {
    const base = options || {};
    const scales = base.scales || {};
    const basePlugins = base.plugins || {};
    const baseLegend = basePlugins.legend || {};
    const baseTooltip = basePlugins.tooltip || {};

    const xBase = scales.x || {};
    const xTicksBase = xBase.ticks || {};
    const maxLabelLength =
      typeof xTicksBase.maxLabelLength === "number" ? xTicksBase.maxLabelLength : 12;

    const x = {
      ...xBase,
      ticks: {
        ...xTicksBase,
        minRotation: 0,
        maxRotation: 0,
        callback: function (value, index, ticks) {
          const raw =
            typeof xTicksBase.callback === "function"
              ? xTicksBase.callback.call(this, value, index, ticks)
              : this && typeof this.getLabelForValue === "function"
                ? this.getLabelForValue(value)
                : value;
          if (!maxLabelLength || maxLabelLength <= 0) return String(raw ?? "");
          return truncateLabel(raw, maxLabelLength);
        },
      },
    };

    const y = {
      ...(scales.y || {}),
      ticks: {
        ...((scales.y && scales.y.ticks) || {}),
        maxTicksLimit: (scales.y && scales.y.ticks && scales.y.ticks.maxTicksLimit) || 6,
        callback: function (value, index, ticks) {
          const existing = (scales.y && scales.y.ticks && scales.y.ticks.callback) || null;
          const raw = typeof existing === "function" ? existing.call(this, value, index, ticks) : value;
          return formatCompactNumber(raw);
        },
        display: false,
      },
    };

    const y1 = scales.y1
      ? {
          ...scales.y1,
          ticks: {
            ...((scales.y1 && scales.y1.ticks) || {}),
            maxTicksLimit:
              (scales.y1 && scales.y1.ticks && scales.y1.ticks.maxTicksLimit) || 6,
            callback: function (value, index, ticks) {
              const existing = (scales.y1 && scales.y1.ticks && scales.y1.ticks.callback) || null;
              const raw =
                typeof existing === "function" ? existing.call(this, value, index, ticks) : value;
              return formatCompactNumber(raw);
            },
            display: false,
          },
        }
      : undefined;

    return {
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      ...base,
      plugins: {
        ...basePlugins,
        legend: {
          ...baseLegend,
          ...(typeof baseLegend.display === "boolean" ? {} : { display: false }),
          ...(baseLegend.position ? {} : { position: "top" }),
        },
        tooltip: {
          ...baseTooltip,
          callbacks: {
            ...(baseTooltip.callbacks || {}),
            title: (items) =>
              items && items[0] ? String(items[0].label || "") : "",
          },
        },
      },
      scales: {
        ...scales,
        x,
        y,
        ...(y1 ? { y1 } : {}),
      },
    };
  }, [options]);

  return (
    <div className="chart-fixed-legend-wrap">
      {datasets.length > 0 ? (
        <div className="chart-fixed-legend" role="presentation">
          {datasets.map((ds, idx) => {
            const rawBg = ds?.backgroundColor;
            const bg = Array.isArray(rawBg) ? rawBg[0] : rawBg;
            const rawBorder = ds?.borderColor;
            const border = Array.isArray(rawBorder) ? rawBorder[0] : rawBorder;
            const color =
              normalizeLegendColor(typeof border === "string" ? border : null) ||
              normalizeLegendColor(typeof bg === "string" ? bg : null) ||
              "rgb(120, 120, 135)";
            return (
              <div key={`${ds?.label || "dataset"}-${idx}`} className="chart-fixed-legend-item">
                <span className="chart-fixed-legend-swatch" style={{ background: color }} />
                <span className="chart-fixed-legend-label">{ds?.label || ""}</span>
              </div>
            );
          })}
        </div>
      ) : null}

      <div className={`chart-axis-layout ${hasRightAxis ? "has-right" : ""}`.trim()}>
        <div className="chart-axis chart-axis--left">
          <div className="chart-axis-ticks">
            {(yTicks.length > 0 ? yTicks : [{ label: "0", y: axisPadding.top }]).map((t, idx) => (
              <div
                key={`${t.label}-${idx}`}
                className="chart-axis-tick"
                style={{ top: `${t.y ?? 0}px` }}
              >
                {t.label}
              </div>
            ))}
          </div>
        </div>

        <div className="chart-scroll-viewport chart-scrollbar">
          <div className="chart-scroll-inner" style={minWidth ? { minWidth, height: "100%" } : { height: "100%" }}>
            <Bar ref={chartRef} data={normalizedData} options={mergedOptions} />
          </div>
        </div>

        {hasRightAxis ? (
          <div className="chart-axis chart-axis--right">
            <div className="chart-axis-ticks">
              {(y1Ticks.length > 0 ? y1Ticks : [{ label: "", y: axisPadding.top }]).map((t, idx) => (
                <div
                  key={`${t.label}-${idx}`}
                  className="chart-axis-tick"
                  style={{ top: `${t.y ?? 0}px` }}
                >
                  {t.label}
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default BarChart;
